import React, { useMemo, useState, useEffect } from 'react';
import { Table, Button, Popconfirm, Input, Space, Tag, Drawer, Descriptions, List, Divider, Empty } from 'antd';
import { useReturn } from '../../store/returnContext';
import { useProduct } from '../../store/productContext';
import { useCustomer } from '../../store/customerContext';
import { ReturnInvoice, ReturnItem } from '../../types/return';
import { EditOutlined, DeleteOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import InvoicePrintHandler from "../../components/Print/InvoicePrintHandler";
import PageNavigation from "../Layout/PageNavigation";

// The API populates `customer` / `items[].product` with the full document, but
// older records still hold a plain id — handle both shapes.
function getCustomerName(customer: any, customerName: string | undefined, customers: any[]): string {
    if (customer && typeof customer === 'object') return customer.name || customerName || '-';
    if (customer) {
        const c = customers.find(x => x.id === customer || x._id === customer);
        return c?.name || customerName || '-';
    }
    return customerName || '-';
}

function getProductName(product: any, products: any[]): string {
    if (!product) return '-';
    if (typeof product === 'object') return product.name || '-';
    const p = products.find(x => x.id === product || x._id === product);
    return p?.name || '-';
}

function formatDate(date: string) {
    return date ? new Date(date).toLocaleDateString() : '-';
}

function formatCurrency(val: number, currency: string) {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 0,
        }).format(val);
    } catch {
        return val;
    }
}

interface Props {
    onEdit: (returnInvoice: ReturnInvoice) => void;
}

const ReturnList: React.FC<Props> = ({ onEdit }) => {
    const { t } = useTranslation();
    const { returns, deleteReturn } = useReturn();
    const { products } = useProduct();
    const { customers, fetchCustomers } = useCustomer();
    const [search, setSearch] = useState<string>('');
    const [selected, setSelected] = useState<ReturnInvoice | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [printInvoice, setPrintInvoice] = useState<ReturnInvoice | null>(null);

    useEffect(() => {
        fetchCustomers?.();
    }, []);

    const filtered = useMemo(() => {
        return returns.filter(r =>
            (r.invoiceNumber ?? '').toLowerCase().includes(search.toLowerCase()) ||
            getCustomerName(r.customer, r.customerName, customers).toLowerCase().includes(search.toLowerCase()) ||
            (r.customerPhone ?? '').toLowerCase().includes(search.toLowerCase())
        );
    }, [returns, search, customers]);

    const openDrawer = (ret: ReturnInvoice) => {
        setSelected(ret);
        setDrawerOpen(true);
    };
    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelected(null);
    };

    const columns = [
        {
            title: t('return_list_invoice'),
            dataIndex: 'invoiceNumber',
            render: (text: string, record: ReturnInvoice) => (
                <Button
                    type="link"
                    onClick={() => openDrawer(record)}
                    icon={<EyeOutlined />}
                    style={{ padding: 0 }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t('return_list_customer'),
            dataIndex: 'customer',
            render: (_: any, record: any) =>
                getCustomerName(record.customer, record.customerName, customers),
        },
        {
            title: t('return_list_customer_phone'), dataIndex: 'customerPhone',
            render: (_: any, record: any) => {
                if (record.customerPhone) return record.customerPhone;
                if (record.customer) {
                    const c = customers.find((x: any) => x.id === record.customer);
                    return c?.phone || '-';
                }
                return '-';
            }
        },
        {
            title: t('return_list_date'),
            dataIndex: 'date',
            render: (d: string) => formatDate(d),
        },
        { title: t('return_list_total'), dataIndex: 'total', render: (val: number, rec: ReturnInvoice) => formatCurrency(val, rec.currency) },
        {
            title: t('return_list_currency'),
            dataIndex: 'currency',
            render: (cur: string) => (
                <Tag color={cur === 'USD' ? 'blue' : 'gold'}>{cur}</Tag>
            ),
        },
        { title: t('return_list_note'), dataIndex: 'note' },
        {
            title: t('return_list_actions'),
            render: (_: any, record: ReturnInvoice) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => onEdit(record)}>
                        {t('return_list_edit')}
                    </Button>
                    <Popconfirm
                        title={t('return_list_delete_confirm') as string}
                        onConfirm={() => deleteReturn(record.id!)}
                    >
                        <Button danger icon={<DeleteOutlined />}>
                            {t('return_list_delete')}
                        </Button>
                    </Popconfirm>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={() => setPrintInvoice(record)}
                    >
                        {t("print")}
                    </Button>
                </Space>
            )
        }
    ];

    // Expanded row for item details
    const expandedRowRender = (record: ReturnInvoice) => (
        <List
            itemLayout="horizontal"
            dataSource={record.items}
            renderItem={(item: ReturnItem, idx) => (
                <List.Item>
                    <List.Item.Meta
                        title={
                            <span>
                                <b>{getProductName(item.product, products)}</b> <Tag>{item.unit}</Tag>
                            </span>
                        }
                        description={
                            <>
                                <span>
                                    {t('return_list_qty')}: {item.qty} | {t('return_list_sell_price')}: {formatCurrency(item.sellPrice, record.currency)}
                                </span>
                                {(item.unit === "meter" || item.unit === "roll") && item.unitLength !== undefined && (
                                    <> | {t('return_list_length')}: {item.unitLength}</>
                                )}
                                {item.note && <> | {t('return_list_item_note')}: {item.note}</>}
                            </>
                        }
                    />
                </List.Item>
            )}
            locale={{ emptyText: <Empty description={t('return_list_no_items')} /> }}
        />
    );

    return (
        <div>
            <PageNavigation title={t('return_list') as string} />
            <Input.Search
                allowClear
                style={{ marginBottom: 16, maxWidth: 300 }}
                placeholder={t('return_list_search_ph') as string}
                onChange={e => setSearch(e.target.value)}
                value={search}
            />
            <Table
                rowKey={record => record.id as string}
                dataSource={filtered}
                columns={columns}
                expandable={{ expandedRowRender }}
                pagination={{ pageSize: 8 }}
                scroll={{ x: 'max-content' }}
                locale={{
                    emptyText: <Empty description={t('return_list_no_returns')} />,
                }}
            />

            <Drawer
                title={
                    selected
                        ? `${t('return_list_return')} ${selected.invoiceNumber}`
                        : t('return_list_details')
                }
                placement="right"
                onClose={closeDrawer}
                open={drawerOpen}
                width={500}
            >
                {selected ? (
                    <>
                        <Descriptions bordered size="small" column={1}>
                            <Descriptions.Item label={t('return_list_customer')}>
                                {getCustomerName(selected.customer, selected.customerName, customers)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('return_list_customer_phone')}>
                                {selected.customerPhone || (selected.customer
                                    ? (customers.find((x: any) => x.id === selected.customer)?.phone || '-')
                                    : '-')}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('return_list_date')}>
                                {formatDate(selected.date)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('return_list_currency')}>
                                <Tag color={selected.currency === 'USD' ? 'blue' : 'gold'}>
                                    {selected.currency}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('return_list_total')}>
                                {formatCurrency(selected.total, selected.currency)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('return_list_note')}>
                                {selected.note || '-'}
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider orientation="left" style={{ marginTop: 16 }}>
                            {t('return_list_items')}
                        </Divider>
                        {expandedRowRender(selected)}
                    </>
                ) : (
                        <Empty description={t('return_list_no_selected')} />
                )}
            </Drawer>

            {/* Print Handler */}
            {printInvoice && (
                <InvoicePrintHandler
                    invoice={printInvoice}
                    type="return"
                    getCustomerOrSupplierName={() => getCustomerName(printInvoice.customer, printInvoice.customerName, customers)}
                    getProductName={(id) => getProductName(id, products)}
                    onAfterPrint={() => setPrintInvoice(null)}
                />
            )}
        </div>
    );
};

export default ReturnList;
